import React, { useState } from 'react';

import { Stack, Typography } from '@mui/material';

import { Button } from '@/components/NewUI/ComponentsLibrary';
import { BODY_TEXT, DARK_GREY, RED } from '@/components/ThemeContext/constants';

import { turnOffSingleRoot } from './mode';

const styles = {
  root: { rowGap: 1 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', columnGap: 1, rowGap: 1 },
  message: { fontSize: '14px', color: BODY_TEXT },
  hint: { fontSize: '12px', color: DARK_GREY },
  error: { fontSize: '12px', color: RED }
};

interface DeadShellControlsProps {
  legacyHref?: string;
  modeUrl?: string;
  message?: string;
  /** Injected in tests; the default PUTs `enabled: false` and reloads. */
  turnOff?: () => Promise<void>;
}

// The escape hatch the shell always offers: open the legacy page, or turn the mode off.
// Used by the error boundary; the Header toggle is the way out while the shell works.
const DeadShellControls = ({ legacyHref, message, modeUrl, turnOff }: DeadShellControlsProps) => {
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  const onTurnOff = () => {
    setPending(true);
    setFailed(false);
    (turnOff ?? (async () => turnOffSingleRoot(modeUrl)))().catch(() => {
      setPending(false);
      setFailed(true);
    });
  };

  return (
    <Stack id="single-root-dead-shell-controls" sx={styles.root}>
      {message ? <Typography sx={styles.message}>{message}</Typography> : null}
      <Stack sx={styles.actions}>
        {legacyHref ? (
          <Button buttonVariant="outline" href={legacyHref} id="single-root-open-legacy">
            Open legacy page
          </Button>
        ) : null}
        <Button
          buttonVariant="secondary"
          id="single-root-turn-off"
          loading={pending}
          onClick={onTurnOff}
        >
          Turn off Single-root
        </Button>
      </Stack>
      <Typography sx={styles.hint}>Internal staff only. Reloads the page.</Typography>
      {failed ? (
        <Typography role="alert" sx={styles.error}>
          Could not turn off Single-root. Reload the page and try again.
        </Typography>
      ) : null}
    </Stack>
  );
};

export default DeadShellControls;
